import React, { useEffect, useState } from "react";
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string } from 'yup';
import { Container, Form, Row, Col } from "react-bootstrap";
import {
  CircularProgress,
  ThemeProvider,
  createTheme,
  Button,
  SvgIcon,
  Alert,
  Dialog,
  DialogContent,
  Typography,
  DialogActions,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import ProductDetails from "../../MCT/Helpers/MCTProductDetails";
import { StyledRadioInput, TextInput } from '../../../Helpers/Inputs';
import CovrButton from '../../../Helpers/Buttons/CovrButton';
import FeatureCard from "../Cards/FeatureCard";
import InputTheme from '../Helpers/InputTheme';
import { CurrencyFormat, InputMaskPhone } from "../../../Helpers/Formatters";
import { ProductDetailsDialog } from "../Cards/MCTQuoteCard";
import CheckmarkIcon from '../../../../icons/checkmark.svg?react';
import { QuoteProductID } from "../QuoteProductID";
import { NextStep } from "./NextStep";
import { ProductDetailsDialog as TermProductDetailsDialog } from "../../CustomizeTerm/QuoterComponents/ProductDetailModel";
import { CurrencyFormatFunc } from "../../../Helpers/Formatters/CurrencyFormat";
import './InsuranceRequest.scss';

export default function InsuranceRequest(props) {
  const [isProductDetailsDialogOpen, setIsProductDetailsDialogOpen] = useState(false);

  const formMethods = useForm({
    resolver: yupResolver(object().shape({
      phone: props.enablePhone ? string().required("Phone Number is required.")
        .matches(/^\(\d{3}\)\s\d{3}-\d{4}$/, 'Phone must be formatted as (123) 456-7890') : string(),
      email: string()
        .email('Email address must be valid.')
        .required('Email is required'),
      usResident: props.showUsResident ? string().required('This field is required') : string().notRequired(),
      existingInsurance: props.showExistingInsurance ? string().required('This field is required') : string().notRequired(),
      replacementIndicator: string().when('existingInsurance', {
        is: (existingInsurance) => existingInsurance === "true" && props.showExistingInsurance,
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.notRequired()
      }),
      firstName: string()
        .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
        .required('First Name is required.'),
      lastName: string()
        .matches(/^\s*$|^[ a-zA-ZÀ-ÖØ-öø-ÿ'.-]+$/, 'Numbers not allowed.')
        .required('Last Name is required.'),
    })),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState || {},
  });

  const { handleSubmit, setValue } = formMethods;

  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [usResident, setUsResident] = useState("");
  const [existingInsurance, setExistingInsurance] = useState("");
  const [replacementIndicator, setReplacementIndicator] = useState("");
  const showUsResidentErrorMessage = usResident === "false";
  const showReplacementErrorMessage = existingInsurance === "true" && replacementIndicator === "true";

  useEffect(() => {
    if (props.basicInfo) {
      setFirstName(props.basicInfo.firstName);
      setLastName(props.basicInfo.lastName);
      setEmail(props.basicInfo.email);
      setValue('firstName', props.basicInfo.firstName);
      setValue('lastName', props.basicInfo.lastName);
      setValue('email', props.basicInfo.email);
    }
  }, []);

  const onSubmit = (isReAttempt = false) => {
    setLoading(true);
    const submitRequest = {
      email,
      firstName,
      lastName,
      phone,
      setLoadingFromComponent: setLoading,
      usResident,
      replacementIndicator: existingInsurance === "true" ? replacementIndicator : existingInsurance,
      isReAttempt: isReAttempt
    };
    props.onSubmit(submitRequest);
  };

  const ResumeCaseModal = () => {

    const handleClose = (event, reason) => {
      // Prevent closing if the reason is a backdrop click or an escape key press
      if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
        return;
      }
      props.setShowResumeCaseModal(false);
    };

    const handleNewApplication = () => {
      handleClose();
      onSubmit(true);
    }
    const handleResume = () => {
      props.setShowResumeCaseModal(false);
      if (props.apiResponseData?.handOffURL) {
        window.location.replace(props.apiResponseData.handOffURL);
      }
    };

    return (
      <Dialog open={props.showResumeCaseModal} onClose={handleClose} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogContent>
          <Typography className="mb-2">Hi {firstName}</Typography>
          {props.apiResponseData?.recordStatus === 0
            ? <Typography variant="body1" color="textSecondary">
              It looks like your application is still in progress. <br />
              Please wait a few moments and then try clicking Submit again.
            </Typography>
            : <Typography variant="body1" color="textSecondary">
              It looks like you already started an application with Prudential. <br />
              Would you like to resume it?
            </Typography>
          }
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          {props.apiResponseData?.recordStatus === 0
            ? <Button onClick={handleClose} variant="contained" color="primary">
              Close
            </Button>
            : <React.Fragment>
              <Button onClick={handleNewApplication} variant="outlined" color="secondary">
                New Application
              </Button>
              <Button onClick={handleResume} variant="contained" color="primary">
                Resume
              </Button>
            </React.Fragment>
          }
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <ThemeProvider theme={(theme) => createTheme({
      ...theme,
      ...InputTheme(),
    })}>
      <div>
        <Helmet>
          <title>{`Insurance Request`}</title>
        </Helmet>
        <Container className="request-wrapper">
          <Row className="request-container d-flex">
            <ReviewContainer quote={props.quoterFormState} isProductDetailsDialogOpen={isProductDetailsDialogOpen} setIsProductDetailsDialogOpen={setIsProductDetailsDialogOpen}
              planFeature={props.planFeature} />
            <Col md={6} className="d-flex justify-content-center w-100">
              <div className="contact-info-container">
                {props.errorMessage &&
                  <Alert severity="error" className="mb-2">
                    <div> {props.errorMessage}</div>
                  </Alert>
                }
                <Row className="flex-column align-items-center">
                  <div className={`icon-container background-color-16`}>
                    <SvgIcon component={CheckmarkIcon} color='primary' />
                  </div>
                  <h2 className="request-title text-center">Let’s start your online application</h2>
                </Row>
                <Row className="justify-content-center">
                  <p className="text-center request-line">Get approved in ten minutes</p>
                </Row>
                <FormProvider {...formMethods}>
                  <Form onSubmit={handleSubmit(() => onSubmit(false))}>
                    <Row className="justify-content-center">
                      <Col lg={12} className="w-100">
                        <div className="form-group">
                          <TextInput
                            name="firstName"
                            id="firstName"
                            label="First Name"
                            value={firstName}
                            onChange={e => setFirstName(e.target.value)}
                          />
                        </div>
                        <div className="form-group">
                          <TextInput
                            name="lastName"
                            id="lastName"
                            label="Last Name"
                            value={lastName}
                            onChange={e => setLastName(e.target.value)}
                          />
                        </div>
                        <div className={props.enablePhone ? 'form-group' : 'mb-0'}>
                          <TextInput
                            name="email"
                            id="email"
                            label="Email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                          />
                        </div>

                        {props.enablePhone && <div className={props.showUsResident ? 'form-group' : "mb-0"}>
                          <TextInput
                            name="phone"
                            id="phone"
                            label="Phone Number"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            InputProps={{
                              inputComponent: InputMaskPhone,
                            }}
                          />
                        </div>}

                        {props.showUsResident &&
                          <React.Fragment>
                            <div className="mb-0">
                              <StyledRadioInput
                                name="usResident"
                                id="usResident"
                                label="Are you a permanent, legal United States resident?"
                                value={usResident}
                                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                                onChange={e => setUsResident(e)}
                                isBlankIcon={true}
                                isBlankCheckMark={false}
                              />
                            </div>
                            {showUsResidentErrorMessage &&
                              <p className="error-message">Unfortunately, this product requires you to be a permanent, legal US Resident. Please return to the quote results screen and select another product.</p>
                            }
                          </React.Fragment>
                        }

                        {props.showExistingInsurance &&
                          <React.Fragment>
                            <div className="mb-0">
                              <StyledRadioInput
                                name="existingInsurance"
                                id="existingInsurance"
                                label="Do you have any existing life insurance policies or annuity contracts?"
                                value={existingInsurance}
                                options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                                onChange={e => setExistingInsurance(e)}
                                isBlankIcon={true}
                                isBlankCheckMark={false}
                              />
                            </div>
                            {existingInsurance === "true" &&
                              <div className="mb-0">
                                <StyledRadioInput
                                  name="replacementIndicator"
                                  id="replacementIndicator"
                                  label="By applying for this life insurance, do you intend to replace, discontinue or change any existing inforce policy or contract?"
                                  value={replacementIndicator}
                                  options={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                                  onChange={e => setReplacementIndicator(e)}
                                  isBlankIcon={true}
                                  isBlankCheckMark={false}
                                />
                              </div>
                            }
                            {showReplacementErrorMessage &&
                              <p className="error-message">Unfortunately, this product does not allow for replacement of existing insurance. Please return to the quote results screen and select another product.</p>
                            }
                          </React.Fragment>
                        }
                      </Col>
                    </Row>
                    {(showUsResidentErrorMessage || showReplacementErrorMessage || props.errorMessage) ?
                      <Row className="form-button-container">
                        <Col className="pe-2 ps-0">
                          <Button variant="outlined" className="insurance-back-button" fullWidth size="medium" color="primary" onClick={props.onBack}>Back to Results</Button>
                        </Col>
                      </Row>
                      : (props.isLoading ?
                        <Row className="form-button-container">
                          <div className="submit-loading-container">
                            <CircularProgress />
                          </div>
                          <p className="loader-content">{props.handoffMessage}</p>
                        </Row> :
                        <Row className="form-button-container">
                          <Col className="d-flex justify-content-center pe-2 ps-0">
                            <Button variant="outlined" className="insurance-back-button" fullWidth size="medium" color="primary" onClick={props.onBack}>Back</Button>
                          </Col>
                          <Col className="d-flex justify-content-center ps-2 pe-0">
                            <CovrButton className="submitButton" name="Continue" loading={loading} fullWidth />
                          </Col>
                        </Row>
                      )}
                  </Form>
                </FormProvider>
                <Row className="disclaimer-container">
                  {!props.isLoading && props.disclaimer}
                </Row>
              </div>
            </Col>
          </Row>
        </Container>
        {props.showCustomizeTermModal ? <TermProductDetailsDialog
          isOpen={isProductDetailsDialogOpen}
          productDetails={
            <ProductDetails
              carrierName={props.quoterFormState?.selectedQuote?.carrierName}
              productDescription={props.quoterFormState?.selectedQuote?.productDescription}
              hasPermConversion={props.quoterFormState?.selectedQuote?.hasConvertibility}
              productInformation={props.quoterFormState?.selectedQuote?.productInformation}
            />
          }
          quoteCardProps={props.quoterFormState?.selectedQuote}
          hideSelect
          onClose={() => setIsProductDetailsDialogOpen(false)}
        /> :
          <ProductDetailsDialog
            isOpen={isProductDetailsDialogOpen}
            productDetails={
              <ProductDetails
                carrierName={props.quoterFormState?.selectedQuote?.carrierName}
                productDescription={props.quoterFormState?.selectedQuote?.productDescription}
                hasPermConversion={props.quoterFormState?.selectedQuote?.hasConvertibility}
                productInformation={props.quoterFormState?.selectedQuote?.productInformation}
              />
            }
            quoteCardProps={props.quoterFormState?.selectedQuote}
            hideSelect
            onClose={() => setIsProductDetailsDialogOpen(false)}
          />
        }
        {props.showResumeCaseModal && <ResumeCaseModal />}
      </div>
    </ThemeProvider>
  );
}

function ReviewContainer(props) {
  const { quote, planFeature, setIsProductDetailsDialogOpen } = props;
  const isPrudentialProduct = quote?.selectedQuote?.productID === QuoteProductID.PRUDENTIAL_SIMPLY_TERM_PRODUCT_ID;

  const [currentFeatureStep, setCurrentFeatureStep] = useState(1);
  const planFeatureLength = planFeature.length;

  const setFeatureStep = () => {
    if ((currentFeatureStep + 1) > (planFeatureLength)) {
      setCurrentFeatureStep(1);
    } else {
      setCurrentFeatureStep(currentFeatureStep + 1);
    }
  };

  useEffect(() => {
    const slideTimeOut = 4500;
    const timeout = setTimeout(setFeatureStep, slideTimeOut);

    return () => clearTimeout(timeout);
  }, [currentFeatureStep]);

  return (
    <Col md={6} className="review-container d-flex align-items-center w-100">
      <div className="coverage-detail-container">
        <Row className="coverage-detail">
          <Col className="carrier-container d-flex justify-content-center align-items-center pe-0">
            <img src={quote.carrierLogoUrl} alt="Carrier Logo" className={(quote.carrierName?.toLowerCase() === "lga") ? "lg" : ""} />
            <p className="primary-color">{quote.selectedQuote?.rating} Rating</p>
          </Col>
          <Col className="coverage-container d-flex justify-content-center align-items-start ps-0">
            <div className="coverage">
              <h2>Coverage Amount</h2>
              <p className="coverage-amount"><CurrencyFormat number={quote.coverageAmount} whole /></p>
              <p className="quote-amount">{`${CurrencyFormatFunc(quote.premium)}/mo`} <span>{quote.term || quote.termLength} Year Term</span></p>
              <button type="button" className="detailButton" onClick={() => setIsProductDetailsDialogOpen(true)}>Approval Process</button>
            </div>
          </Col>
        </Row>
      </div>
      <div className="request-step-container">
        <h2 className="section-title text-center">Easy next steps</h2>
        <NextStep submissionTime={isPrudentialProduct ? '10' : '15'} />
      </div>
      <div className="plan-feature-container">
        <h2 className="section-title text-center">Your plan’s best features</h2>
        <div className="text-slideshow">
          <ul className={`step${currentFeatureStep}`}>
            {props.planFeature.map((f, index) => <FeatureCard planFeature={f} key={index} />)}
          </ul>
        </div>
      </div>
    </Col>
  );
}

InsuranceRequest.propTypes = {
  quoterFormState: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  planFeature: PropTypes.array.isRequired,
  enablePhone: PropTypes.bool,
  onBack: PropTypes.func,
  isLoading: PropTypes.bool,
  disclaimer: PropTypes.object,
  showCustomizeTermModal: PropTypes.bool,
  basicInfo: PropTypes.object,
};

ReviewContainer.propTypes = {
  planFeature: PropTypes.array.isRequired,
  quote: PropTypes.object,
  setIsProductDetailsDialogOpen: PropTypes.func,
};

InsuranceRequest.DefaultFormState = (prefill = false, basicInfo = null) => {
  return {
    firstName: prefill ? 'Example' : (basicInfo && basicInfo.firstName) || '',
    lastName: prefill ? 'Consumer' : (basicInfo && basicInfo.lastName) || '',
    email: prefill ? 'user@address.com' : (basicInfo && basicInfo.email) || '',
    phone: prefill ? '(555) 555-1234' : (basicInfo && basicInfo.phone) || '',
    usResident: prefill ? 'true' : (basicInfo && basicInfo.usResident) || '',
    existingInsurance: prefill ? 'true': '',
    replacementIndicator: prefill ? 'false' : '',
  };
};
